import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { getAllCronTasksOptions } from "@/lib/client/@tanstack/react-query.gen";
import type {
	CronTaskDto,
	ExecuteCronTaskRequest,
} from "@/lib/client/types.gen";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
	Calendar,
	Play,
	Clock,
	AlertCircle,
	CheckCircle,
	Loader,
} from "lucide-react";
import { executeCronTask } from "@/lib/client";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/configuration/cron-tasks")({
	component: CronTasksPage,
});

function CronTasksPage() {
	const [executionDate, setExecutionDate] = useState(
		format(new Date(), "yyyy-MM-dd'T'HH:mm"),
	);
	const [executionResult, setExecutionResult] = useState<{
		task: string;
		message: string;
	} | null>(null);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [selectedTask, setSelectedTask] = useState<CronTaskDto | null>(null);
	const [error, setError] = useState("");

	const {
		data: cronTasks,
		isLoading,
		error: loadError,
	} = useQuery(getAllCronTasksOptions());

	const executeMutation = useMutation({
		mutationFn: (request: ExecuteCronTaskRequest) =>
			executeCronTask({ body: request }),
		onSuccess: (response, variables) => {
			if (response.data) {
				setExecutionResult({
					task: variables.taskName!,
					message: response.data.message || "Tâche exécutée avec succès",
				});
			}
			setSelectedTask(null);
			setError("");
		},
		onError: (error: any, variables) => {
			console.error("Erreur lors de l'exécution:", error);
			setError(
				`Erreur lors de l'exécution de ${variables.taskName}: ${error.message || "Erreur inconnue"}`,
			);
			setSelectedTask(null);
		},
	});

	function handleExecuteClick(task: CronTaskDto) {
		setSelectedTask(task);
		setShowConfirmModal(true);
	}

	async function confirmExecute() {
		if (!selectedTask) return;

		setShowConfirmModal(false);
		setExecutionResult(null);
		setError("");

		const request: ExecuteCronTaskRequest = {
			taskName: selectedTask.name!,
			executionDate:
				executionDate.includes(":") && executionDate.split(":").length === 2
					? `${executionDate}:00`
					: executionDate,
		};

		executeMutation.mutate(request);
	}

	function getTaskIcon(taskName?: string) {
		if (!taskName) return <Clock className="h-5 w-5 text-gray-500" />;

		if (taskName.includes("notification")) {
			return <AlertCircle className="h-5 w-5 text-blue-500" />;
		}
		if (taskName.includes("automatic")) {
			return <Play className="h-5 w-5 text-green-500" />;
		}
		if (taskName.includes("retry")) {
			return <Clock className="h-5 w-5 text-orange-500" />;
		}
		if (taskName.includes("reminder")) {
			return <AlertCircle className="h-5 w-5 text-yellow-500" />;
		}

		return <Clock className="h-5 w-5 text-gray-500" />;
	}

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-96">
				<Loader className="h-8 w-8 animate-spin text-blue-600" />
			</div>
		);
	}

	if (loadError) {
		return (
			<div className="p-6">
				<div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
					<AlertCircle className="h-5 w-5 mr-2 inline" />
					Erreur lors du chargement des tâches cron
				</div>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900">Tâches Cron</h1>
				<p className="text-gray-600 mt-2">
					Exécuter manuellement les tâches cron avec une date simulée pour
					tester leur fonctionnement
				</p>
			</div>

			{error && (
				<div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center">
					<AlertCircle className="h-5 w-5 mr-2" />
					{error}
				</div>
			)}

			{executionResult && (
				<div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center">
					<CheckCircle className="h-5 w-5 mr-2" />
					<div>
						<strong>{executionResult.task}</strong>: {executionResult.message}
					</div>
				</div>
			)}

			<div className="bg-white rounded-lg shadow mb-6">
				<div className="p-6 border-b">
					<div className="flex items-center gap-4">
						<div className="flex-1">
							<label
								htmlFor="executionDate"
								className="block text-sm font-medium text-gray-700 mb-2"
							>
								Date d'exécution simulée
							</label>
							<div className="relative">
								<Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
								<input
									type="datetime-local"
									id="executionDate"
									className="pl-10 pr-4 py-2 w-full max-w-md border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									value={executionDate}
									onChange={(e) => setExecutionDate(e.target.value)}
								/>
							</div>
							<p className="text-xs text-gray-500 mt-1">
								Cette date sera utilisée pour simuler l'exécution de la tâche
								cron
							</p>
						</div>
					</div>
				</div>

				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Tâche
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Description
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Planification
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Statut
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Action
								</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{cronTasks?.map((task) => {
								const isExecuting =
									executeMutation.isPending && selectedTask?.name === task.name;

								return (
									<tr key={task.name} className="hover:bg-gray-50">
										<td className="px-6 py-4 whitespace-nowrap">
											<div className="flex items-center gap-2">
												{getTaskIcon(task.name)}
												<span className="font-medium text-gray-900">
													{task.name}
												</span>
											</div>
										</td>
										<td className="px-6 py-4">
											<div className="text-sm text-gray-900">
												{task.description}
											</div>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<code className="text-xs bg-gray-100 px-2 py-1 rounded">
												{task.schedule}
											</code>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											{task.enabled ? (
												<span className="inline-flex px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">
													Activé
												</span>
											) : (
												<span className="inline-flex px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 rounded-full">
													Désactivé
												</span>
											)}
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<button
												onClick={() => handleExecuteClick(task)}
												disabled={executeMutation.isPending || !task.enabled}
												className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
													isExecuting
														? "bg-gray-100 text-gray-400 cursor-not-allowed"
														: task.enabled
															? "bg-blue-600 text-white hover:bg-blue-700"
															: "bg-gray-100 text-gray-400 cursor-not-allowed"
												}`}
											>
												{isExecuting ? (
													<>
														<Loader className="h-4 w-4 animate-spin" />
														Exécution...
													</>
												) : (
													<>
														<Play className="h-4 w-4" />
														Exécuter
													</>
												)}
											</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>

			<Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Confirmer l'exécution</DialogTitle>
						<DialogDescription>
							Êtes-vous sûr de vouloir exécuter la tâche{" "}
							<strong>{selectedTask?.name}</strong> avec la date simulée{" "}
							<strong>
								{format(new Date(executionDate), "dd/MM/yyyy HH:mm", {
									locale: fr,
								})}
							</strong>{" "}
							?
						</DialogDescription>
					</DialogHeader>

					<div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
						<p className="text-sm text-yellow-800">
							<strong>Attention :</strong> Cette action va exécuter réellement
							la tâche avec la date simulée. Les notifications seront envoyées
							et les paiements traités selon les données actuelles.
						</p>
					</div>

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setShowConfirmModal(false)}
						>
							Annuler
						</Button>
						<Button onClick={confirmExecute}>Confirmer l'exécution</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
